import React, { useState, useEffect } from 'react';
import { Lock, Plus, Edit2, Trash2, Eye, EyeOff, ExternalLink, Search, X, Tag, Globe, Pencil, Check } from 'lucide-react';
import { apiCall } from '../services/api';
import { passwordsMockData } from '../data/mockData'; // Import mockup data
import { toast } from 'sonner';

const Passwords = () => {
  const [passwords, setPasswords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingPassword, setEditingPassword] = useState(null);
  const [visiblePasswords, setVisiblePasswords] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [importing, setImporting] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    service_name: '',
    url: '',
    username: '',
    password: '',
    category: 'Personal',
    tags: [],
    icon_url: '',
    notes: ''
  });
  const [tagInput, setTagInput] = useState('');

  // Default password categories
  const defaultPasswordCategories = [
    { id: 'all', name: 'All', icon: '📋' },
    { id: 'banking', name: 'Banking', icon: '🏦' },
    { id: 'business', name: 'Business', icon: '💼' },
    { id: 'social', name: 'Social', icon: '👥' },
    { id: 'streaming', name: 'Streaming', icon: '📺' },
    { id: 'dev', name: 'Dev', icon: '💻' },
    { id: 'personal', name: 'Personal', icon: '👤' },
    { id: 'shopping', name: 'Shopping', icon: '🛒' },
    { id: 'entertainment', name: 'Entertainment', icon: '🎮' }
  ];
  
  // Load custom categories from localStorage
  const [customPasswordCategories, setCustomPasswordCategories] = useState(() => {
    try {
      const saved = localStorage.getItem('password_categories');
      return saved ? JSON.parse(saved) : defaultPasswordCategories;
    } catch { return defaultPasswordCategories; }
  });
  
  // Editable category state
  const [showPasswordCategoryModal, setShowPasswordCategoryModal] = useState(false);
  const [newPasswordCategoryName, setNewPasswordCategoryName] = useState('');
  const [newPasswordCategoryIcon, setNewPasswordCategoryIcon] = useState('🔑');
  const [editingPasswordCategory, setEditingPasswordCategory] = useState(null);
  const [editPasswordCategoryValue, setEditPasswordCategoryValue] = useState('');
  
  const passwordCategoryIcons = ['📋', '🏦', '💼', '👥', '📺', '💻', '👤', '🛒', '🎮', '🔑', '🔒', '📧', '☁️', '🎵', '📱', '🌐', '🏠', '💰', '📚', '🎯'];
  
  // Category management functions
  const addPasswordCategory = () => {
    if (!newPasswordCategoryName.trim()) return;
    const categoryId = newPasswordCategoryName.toLowerCase().replace(/\s+/g, '-');
    if (customPasswordCategories.some(c => c.id === categoryId)) { toast.error('Category already exists'); return; }
    const newCat = { id: categoryId, name: newPasswordCategoryName, icon: newPasswordCategoryIcon };
    const updated = [...customPasswordCategories, newCat];
    setCustomPasswordCategories(updated);
    localStorage.setItem('password_categories', JSON.stringify(updated));
    setNewPasswordCategoryName('');
    setNewPasswordCategoryIcon('🔑');
    toast.success(`Added "${newPasswordCategoryName}" category`);
  };
  
  const updatePasswordCategory = (oldId, newName) => {
    if (!newName.trim()) return;
    const newId = newName.toLowerCase().replace(/\s+/g, '-');
    const updated = customPasswordCategories.map(c => c.id === oldId ? { ...c, id: newId, name: newName } : c);
    setCustomPasswordCategories(updated);
    localStorage.setItem('password_categories', JSON.stringify(updated));
    if (selectedCategory === oldId) setSelectedCategory(newId);
    setEditingPasswordCategory(null);
    setEditPasswordCategoryValue('');
    toast.success(`Category renamed to "${newName}"`);
  };
  
  const deletePasswordCategory = (categoryId) => {
    if (['all', 'personal', 'banking'].includes(categoryId)) { toast.error('Cannot delete default category'); return; }
    const updated = customPasswordCategories.filter(c => c.id !== categoryId);
    setCustomPasswordCategories(updated);
    localStorage.setItem('password_categories', JSON.stringify(updated));
    if (selectedCategory === categoryId) setSelectedCategory('all');
    toast.success('Category deleted');
  };

  const categories = customPasswordCategories.map(c => c.name);

  useEffect(() => {
    loadPasswords();
  }, []);

  const loadPasswords = async () => {
    try {
      // USING MOCKUP DATA - Don't fetch from API
      console.log('🔐 Using mockup passwords data:', passwordsMockData.length);
      setPasswords(passwordsMockData);
      // const data = await apiCall('/passwords/?user_id=dwoodson');
      // setPasswords(data);
    } catch (error) {
      console.error('Error loading passwords:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditing && editingPassword) {
        await apiCall(`/passwords/${editingPassword.id}`, {
          method: 'PUT',
          body: JSON.stringify(formData)
        });
      } else {
        await apiCall('/passwords/', {
          method: 'POST',
          body: JSON.stringify({ ...formData, user_id: 'dwoodson' })
        });
      }
      await loadPasswords();
      resetForm();
    } catch (error) {
      console.error('Error saving password:', error);
      alert('Failed to save password');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this password?')) return;
    
    try {
      await apiCall(`/passwords/${id}`, { method: 'DELETE' });
      await loadPasswords();
    } catch (error) {
      console.error('Error deleting password:', error);
      alert('Failed to delete password');
    }
  };

  const handleImportFromContacts = async () => {
    if (!window.confirm('This will scan your iCloud contacts for password information and import them. Continue?')) {
      return;
    }

    setImporting(true);
    try {
      const result = await apiCall('/passwords/import-from-contacts/?user_id=dwoodson', {
        method: 'POST'
      });
      
      await loadPasswords();
      
      alert(`✅ Import complete!\n\n${result.imported} passwords imported\n${result.skipped} skipped (duplicates or incomplete data)\n\nPasswords have been tagged with "imported-from-contacts"`);
    } catch (error) {
      console.error('Error importing passwords:', error);
      alert('❌ Failed to import passwords from contacts');
    } finally {
      setImporting(false);
    }
  };

  const handleEdit = (password) => {
    setFormData({
      service_name: password.service_name,
      url: password.url || '',
      username: password.username,
      password: password.password,
      category: password.category || 'Personal',
      tags: password.tags || [],
      icon_url: password.icon_url || '',
      notes: password.notes || ''
    });
    setEditingPassword(password);
    setIsEditing(true);
  };

  const resetForm = () => {
    setFormData({
      service_name: '',
      url: '',
      username: '',
      password: '',
      category: 'Personal',
      tags: [],
      icon_url: '',
      notes: ''
    });
    setTagInput('');
    setIsCreating(false);
    setIsEditing(false);
    setEditingPassword(null);
  };

  const togglePasswordVisibility = (id) => {
    const newVisible = new Set(visiblePasswords);
    if (newVisible.has(id)) {
      newVisible.delete(id);
    } else {
      newVisible.add(id);
    }
    setVisiblePasswords(newVisible);
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({ ...formData, tags: [...formData.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setFormData({ ...formData, tags: formData.tags.filter(t => t !== tag) });
  };

  const filteredPasswords = passwords.filter(pwd => {
    const matchesSearch = searchQuery === '' || 
      pwd.service_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      pwd.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      pwd.url?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = selectedCategory === 'all' || pwd.category?.toLowerCase() === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-white">Loading passwords...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-3 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4 md:mb-6">
          <div className="flex items-center gap-2 md:gap-3">
            <Lock className="text-white" size={24} />
            <h1 className="text-xl md:text-3xl font-bold text-white">Password Manager</h1>
          </div>
          <div className="flex gap-2 md:gap-3 w-full sm:w-auto">
            <button
              onClick={handleImportFromContacts}
              disabled={importing}
              className={`flex items-center justify-center gap-1 md:gap-2 ${importing ? 'bg-black' : 'bg-green-600 hover:bg-green-700'} text-white px-2 md:px-4 py-2 rounded-lg transition-colors text-xs md:text-base flex-1 sm:flex-auto`}
              title="Import passwords from iCloud contacts"
            >
              <Globe size={16} className="md:hidden" />
              <Globe size={20} className="hidden md:block" />
              <span className="hidden sm:inline">{importing ? 'Importing...' : 'Import from Contacts'}</span>
              <span className="sm:hidden">{importing ? '...' : 'Import'}</span>
            </button>
            <button
              onClick={() => setIsCreating(true)}
              className="flex items-center justify-center gap-1 md:gap-2 bg-gradient-to-r bg-white hover:bg-gray-100 text-black px-2 md:px-4 py-2 rounded-lg transition-colors text-xs md:text-base flex-1 sm:flex-auto"
            >
              <Plus size={16} className="md:hidden" />
              <Plus size={20} className="hidden md:block" />
              <span className="hidden sm:inline">Add Password</span>
              <span className="sm:hidden">Add</span>
            </button>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="bg-transparent border border-white/60 rounded-lg p-3 md:p-4 mb-4 md:mb-6">
          <div className="flex flex-col gap-3 md:gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white" size={18} />
              <input
                type="text"
                placeholder="Search passwords..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/60 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 text-sm md:text-base"
              />
            </div>

            {/* Category Filter - Horizontal scroll on mobile */}
            <div className="flex gap-2 overflow-x-auto pb-1 -mx-3 px-3 md:mx-0 md:px-0 md:flex-wrap items-center">
              {customPasswordCategories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  data-testid={`password-category-${cat.id}`}
                  className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg text-xs md:text-sm transition-colors whitespace-nowrap flex-shrink-0 flex items-center gap-1.5 ${
                    selectedCategory === cat.id
                      ? 'bg-white text-black'
                      : 'bg-white/5 text-white hover:bg-white/10'
                  }`}
                >
                  <span>{cat.icon}</span>
                  {cat.name}
                </button>
              ))}
              
              {/* Edit Categories Button */}
              <button
                onClick={() => setShowPasswordCategoryModal(true)}
                data-testid="edit-password-categories-btn"
                className="px-3 py-1.5 md:py-2 rounded-lg text-xs md:text-sm font-medium transition-all whitespace-nowrap flex-shrink-0 bg-transparent border border-dashed border-gray-500 text-gray-400 hover:bg-gray-800 hover:text-white flex items-center gap-1"
              >
                <Pencil size={12} />
                Edit
              </button>
            </div>
          </div>
        </div>

        {/* Passwords Table - Desktop / Cards - Mobile */}
        <div className="bg-transparent border border-white/60 rounded-lg overflow-hidden">
          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto" style={{ maxHeight: '750px', overflowY: 'auto' }}>
            <table className="w-full">
              <thead className="bg-black border-b border-white/60 sticky top-0 z-10">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-white">Service</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-white">Username</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-white">Password</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-white">Category</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-white">Last Updated</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-white">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {filteredPasswords.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-white">
                      No passwords found. Click "Add Password" to create one.
                    </td>
                  </tr>
                ) : (
                  filteredPasswords.map((pwd) => (
                    <tr key={pwd.id} className="hover:bg-white/5 transition-colors">
                      {/* Service */}
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          {pwd.icon_url ? (
                            <img src={pwd.icon_url} alt="" className="w-8 h-8 rounded" />
                          ) : (
                            <div className="w-8 h-8 bg-white rounded flex items-center justify-center">
                              <Globe size={16} className="text-black" />
                            </div>
                          )}
                          <div>
                            <div className="text-white font-medium">{pwd.service_name}</div>
                            {pwd.url && (
                              <a
                                href={pwd.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-white text-xs hover:underline flex items-center gap-1"
                              >
                                {new URL(pwd.url).hostname}
                                <ExternalLink size={12} />
                              </a>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Username */}
                      <td className="px-6 py-4">
                        <div className="text-white">{pwd.username}</div>
                      </td>

                      {/* Password */}
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <span className="text-white font-mono">
                            {visiblePasswords.has(pwd.id) ? pwd.password : '••••••••'}
                          </span>
                          <button
                            onClick={() => togglePasswordVisibility(pwd.id)}
                            className="text-white hover:text-white transition-colors"
                          >
                            {visiblePasswords.has(pwd.id) ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                        </div>
                      </td>

                      {/* Category */}
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center gap-1 px-3 py-1 bg-transparent border border-white/60 text-white rounded-full text-xs">
                          {pwd.category}
                        </span>
                        {pwd.tags && pwd.tags.length > 0 && (
                          <div className="flex gap-1 mt-1 flex-wrap">
                            {pwd.tags.map(tag => (
                              <span key={tag} className="inline-flex items-center gap-1 px-2 py-0.5 bg-transparent border border-white/60 text-white rounded text-xs">
                                <Tag size={10} />
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>

                      {/* Last Updated */}
                      <td className="px-6 py-4">
                        <div className="text-white text-sm">{formatDate(pwd.updated_at)}</div>
                      </td>

                      {/* Actions */}
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleEdit(pwd)}
                            className="text-white hover:text-white transition-colors"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(pwd.id)}
                            className="text-red-400 hover:text-red-300 transition-colors"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden p-3 space-y-3" style={{ maxHeight: '600px', overflowY: 'auto' }}>
            {filteredPasswords.length === 0 ? (
              <div className="text-center text-white py-8">
                No passwords found. Tap "Add" to create one.
              </div>
            ) : (
              filteredPasswords.map((pwd) => (
                <div key={pwd.id} className="bg-white/5 border border-white/30 rounded-lg p-3">
                  {/* Service Header */}
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      {pwd.icon_url ? (
                        <img src={pwd.icon_url} alt="" className="w-8 h-8 rounded flex-shrink-0" />
                      ) : (
                        <div className="w-8 h-8 bg-white rounded flex items-center justify-center flex-shrink-0">
                          <Globe size={14} className="text-black" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="text-white font-medium text-sm truncate">{pwd.service_name}</div>
                        {pwd.url && (
                          <a
                            href={pwd.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gray-400 text-[10px] hover:underline flex items-center gap-1"
                          >
                            {new URL(pwd.url).hostname}
                            <ExternalLink size={10} />
                          </a>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                      <button
                        onClick={() => handleEdit(pwd)}
                        className="text-white hover:text-gray-300 transition-colors p-1"
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(pwd.id)}
                        className="text-red-400 hover:text-red-300 transition-colors p-1"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>

                  {/* Username */}
                  <div className="mb-2">
                    <span className="text-gray-400 text-[10px]">Username</span>
                    <div className="text-white text-xs truncate">{pwd.username}</div>
                  </div>

                  {/* Password */}
                  <div className="mb-2">
                    <span className="text-gray-400 text-[10px]">Password</span>
                    <div className="flex items-center gap-2">
                      <span className="text-white font-mono text-xs">
                        {visiblePasswords.has(pwd.id) ? pwd.password : '••••••••'}
                      </span>
                      <button
                        onClick={() => togglePasswordVisibility(pwd.id)}
                        className="text-white hover:text-gray-300 transition-colors"
                      >
                        {visiblePasswords.has(pwd.id) ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                    </div>
                  </div>

                  {/* Category & Tags */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center px-2 py-0.5 bg-transparent border border-white/60 text-white rounded-full text-[10px]">
                      {pwd.category}
                    </span>
                    {pwd.tags && pwd.tags.slice(0, 2).map(tag => (
                      <span key={tag} className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-transparent border border-white/40 text-gray-300 rounded text-[10px]">
                        <Tag size={8} />
                        {tag}
                      </span>
                    ))}
                    {pwd.tags && pwd.tags.length > 2 && (
                      <span className="text-gray-400 text-[10px]">+{pwd.tags.length - 2}</span>
                    )}
                  </div>

                  {/* Date */}
                  <div className="mt-2 pt-2 border-t border-white/10">
                    <span className="text-gray-500 text-[10px]">Updated: {formatDate(pwd.updated_at)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Create/Edit Modal */}
        {(isCreating || isEditing) && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-3 md:p-4">
            <div className="bg-black border border-white/60 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-4 md:p-6">
                <div className="flex items-center justify-between mb-4 md:mb-6">
                  <h2 className="text-lg md:text-2xl font-bold text-white">
                    {isEditing ? 'Edit Password' : 'Add New Password'}
                  </h2>
                  <button onClick={resetForm} className="text-white hover:text-gray-300">
                    <X size={20} className="md:hidden" />
                    <X size={24} className="hidden md:block" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-3 md:space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                    {/* Service Name */}
                    <div>
                      <label className="block text-xs md:text-sm font-medium text-white mb-1 md:mb-2">
                        Service / App Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.service_name}
                        onChange={(e) => setFormData({ ...formData, service_name: e.target.value })}
                        className="w-full px-3 md:px-4 py-2 bg-white/5 border border-white/60 rounded-lg text-white text-sm md:text-base focus:outline-none focus:border-blue-500"
                        placeholder="e.g., Chase Bank, Gmail"
                      />
                    </div>

                    {/* URL */}
                    <div>
                      <label className="block text-xs md:text-sm font-medium text-white mb-1 md:mb-2">
                        URL
                      </label>
                      <input
                        type="url"
                        value={formData.url}
                        onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                        className="w-full px-3 md:px-4 py-2 bg-white/5 border border-white/60 rounded-lg text-white text-sm md:text-base focus:outline-none focus:border-blue-500"
                        placeholder="https://example.com/login"
                      />
                    </div>

                    {/* Username */}
                    <div>
                      <label className="block text-xs md:text-sm font-medium text-white mb-1 md:mb-2">
                        Username / Email *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.username}
                        onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                        className="w-full px-3 md:px-4 py-2 bg-white/5 border border-white/60 rounded-lg text-white text-sm md:text-base focus:outline-none focus:border-blue-500"
                        placeholder="your@email.com"
                      />
                    </div>

                    {/* Password */}
                    <div>
                      <label className="block text-xs md:text-sm font-medium text-white mb-1 md:mb-2">
                        Password *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        className="w-full px-3 md:px-4 py-2 bg-white/5 border border-white/60 rounded-lg text-white text-sm md:text-base focus:outline-none focus:border-blue-500"
                        placeholder="Your password"
                      />
                    </div>

                    {/* Category */}
                    <div>
                      <label className="block text-xs md:text-sm font-medium text-white mb-1 md:mb-2">
                        Category
                      </label>
                      <select
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        className="w-full px-3 md:px-4 py-2 bg-white/5 border border-white/60 rounded-lg text-white text-sm md:text-base focus:outline-none focus:border-blue-500"
                      >
                        {categories.filter(c => c !== 'All').map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>

                    {/* Icon URL */}
                    <div>
                      <label className="block text-xs md:text-sm font-medium text-white mb-1 md:mb-2">
                        Icon URL (optional)
                      </label>
                      <input
                        type="url"
                        value={formData.icon_url}
                        onChange={(e) => setFormData({ ...formData, icon_url: e.target.value })}
                        className="w-full px-3 md:px-4 py-2 bg-white/5 border border-white/60 rounded-lg text-white text-sm md:text-base focus:outline-none focus:border-blue-500"
                        placeholder="https://example.com/logo.png"
                      />
                    </div>
                  </div>

                  {/* Tags */}
                  <div>
                    <label className="block text-xs md:text-sm font-medium text-white mb-1 md:mb-2">
                      Tags
                    </label>
                    <div className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                        className="flex-1 px-3 md:px-4 py-2 bg-white/5 border border-white/60 rounded-lg text-white text-sm md:text-base focus:outline-none focus:border-blue-500"
                        placeholder="Add tag and press Enter"
                      />
                      <button
                        type="button"
                        onClick={addTag}
                        className="px-3 md:px-4 py-2 bg-gradient-to-r bg-white hover:bg-gray-100 text-black rounded-lg transition-colors text-sm md:text-base"
                      >
                        Add
                      </button>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {formData.tags.map(tag => (
                        <span
                          key={tag}
                          className="inline-flex items-center gap-1 md:gap-2 px-2 md:px-3 py-1 bg-transparent border border-white/60 text-white rounded-full text-xs md:text-sm"
                        >
                          {tag}
                          <button
                            type="button"
                            onClick={() => removeTag(tag)}
                            className="hover:text-gray-300"
                          >
                            <X size={12} className="md:hidden" />
                            <X size={14} className="hidden md:block" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-xs md:text-sm font-medium text-white mb-1 md:mb-2">
                      Notes
                    </label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      rows="3"
                      className="w-full px-3 md:px-4 py-2 bg-white/5 border border-white/60 rounded-lg text-white text-sm md:text-base focus:outline-none focus:border-blue-500"
                      placeholder="Additional notes, security questions, etc."
                    />
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-2 md:gap-3 pt-2 md:pt-4">
                    <button
                      type="submit"
                      className="flex-1 bg-gradient-to-r bg-white hover:bg-gray-100 text-black py-2.5 md:py-3 rounded-lg font-medium transition-colors text-sm md:text-base"
                    >
                      {isEditing ? 'Update' : 'Save'}
                    </button>
                    <button
                      type="button"
                      onClick={resetForm}
                      className="px-4 md:px-6 py-2.5 md:py-3 bg-transparent hover:bg-white/5 text-white rounded-lg font-medium transition-colors text-sm md:text-base border border-white/30"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Password Category Management Modal */}
        {showPasswordCategoryModal && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowPasswordCategoryModal(false)}>
            <div className="bg-gray-900 rounded-2xl w-full max-w-md border border-gray-700 shadow-2xl" onClick={e => e.stopPropagation()}>
              <div className="p-4 border-b border-gray-700 flex items-center justify-between">
                <h3 className="text-lg font-bold text-white">Edit Password Categories</h3>
                <button onClick={() => setShowPasswordCategoryModal(false)} className="p-2 hover:bg-gray-800 rounded-lg"><X size={18} className="text-gray-400" /></button>
              </div>
              <div className="p-4 border-b border-gray-700">
                <div className="flex gap-2 mb-3">
                  <div className="relative">
                    <button className="w-10 h-10 bg-black border border-gray-600 rounded-lg text-xl flex items-center justify-center" onClick={(e) => e.currentTarget.nextElementSibling.classList.toggle('hidden')}>{newPasswordCategoryIcon}</button>
                    <div className="hidden absolute top-full left-0 mt-1 bg-gray-800 border border-gray-600 rounded-lg p-2 z-10 grid grid-cols-5 gap-1 w-40">
                      {passwordCategoryIcons.map(icon => (<button key={icon} onClick={(e) => { setNewPasswordCategoryIcon(icon); e.currentTarget.parentElement.classList.add('hidden'); }} className="w-7 h-7 hover:bg-gray-700 rounded text-lg flex items-center justify-center">{icon}</button>))}
                    </div>
                  </div>
                  <input type="text" value={newPasswordCategoryName} onChange={(e) => setNewPasswordCategoryName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addPasswordCategory()} placeholder="New category name..." className="flex-1 px-3 py-2 bg-black border border-gray-600 rounded-lg text-white text-sm" data-testid="new-password-category-input" />
                </div>
                <button onClick={addPasswordCategory} disabled={!newPasswordCategoryName.trim()} className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:bg-gray-700 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-1.5" data-testid="add-password-category-btn"><Plus size={14} />Add Category</button>
              </div>
              <div className="p-4 max-h-80 overflow-y-auto space-y-2">
                {customPasswordCategories.filter(c => c.id !== 'all').map(cat => (
                  <div key={cat.id} className="flex items-center gap-2 p-2 bg-gray-800/50 rounded-lg group">
                    {editingPasswordCategory === cat.id ? (
                      <>
                        <span className="text-lg">{cat.icon}</span>
                        <input type="text" value={editPasswordCategoryValue} onChange={(e) => setEditPasswordCategoryValue(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') updatePasswordCategory(cat.id, editPasswordCategoryValue); if (e.key === 'Escape') { setEditingPasswordCategory(null); setEditPasswordCategoryValue(''); }}} className="flex-1 px-2 py-1 bg-black border border-gray-600 rounded text-white text-sm" autoFocus />
                        <button onClick={() => updatePasswordCategory(cat.id, editPasswordCategoryValue)} className="p-1.5 text-green-400 hover:bg-green-500/20 rounded"><Check size={14} /></button>
                        <button onClick={() => { setEditingPasswordCategory(null); setEditPasswordCategoryValue(''); }} className="p-1.5 text-red-400 hover:bg-red-500/20 rounded"><X size={14} /></button>
                      </>
                    ) : (
                      <>
                        <span className="w-8 h-8 rounded-lg bg-gray-700 flex items-center justify-center text-sm">{cat.icon}</span>
                        <span className="flex-1 text-white text-sm">{cat.name}{['personal', 'banking'].includes(cat.id) && <span className="ml-2 text-xs text-gray-500">(default)</span>}</span>
                        <button onClick={() => { setEditingPasswordCategory(cat.id); setEditPasswordCategoryValue(cat.name); }} className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded opacity-0 group-hover:opacity-100"><Pencil size={14} /></button>
                        {!['personal', 'banking'].includes(cat.id) && <button onClick={() => deletePasswordCategory(cat.id)} className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/20 rounded opacity-0 group-hover:opacity-100"><Trash2 size={14} /></button>}
                      </>
                    )}
                  </div>
                ))}
              </div>
              <div className="p-4 border-t border-gray-700">
                <button onClick={() => setShowPasswordCategoryModal(false)} className="w-full px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-sm font-medium" data-testid="done-password-category-btn">Done</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Passwords;
